import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, BookOpen, CheckCircle2, XCircle, Link2, Unlink, Zap } from "lucide-react";
import { toast } from "sonner";
import { XeroSetupGuide } from "./XeroSetupGuide";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

/** Xero credentials + account-code management. Values live in the shared
 * database (settings.xero_config) — editable without a redeploy. */
export const XeroSection = () => {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_id: "", client_secret: "",
    sales_account_code: "", fee_account_code: "",
  });

  const fetchAll = useCallback(async (deep = false) => {
    setLoading(true);
    try {
      const [stRes, cfgRes] = await Promise.all([
        axios.get(`${API}/api/xero/status${deep ? "?force_verify=1" : ""}`, { headers: authHeader() }),
        axios.get(`${API}/api/xero/config`, { headers: authHeader() }),
      ]);
      setStatus(stRes.data);
      setConfig(cfgRes.data);
      // Prefill account codes (not secrets) so the operator sees the
      // effective values and can tweak them in place.
      setForm((f) => ({
        ...f,
        sales_account_code: cfgRes.data.sales_account_code || "",
        fee_account_code: cfgRes.data.fee_account_code || "",
      }));
      if (deep) {
        if (stRes.data.connected) toast.success(`Xero verified — connected to ${stRes.data.tenant_name}`);
        else toast.error(stRes.data.detail || "Xero verification failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Xero config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(false); }, [fetchAll]);

  const handleConnect = async () => {
    try {
      const res = await axios.get(`${API}/api/xero/authorize-url`, { headers: authHeader() });
      window.open(res.data.url, "_blank", "width=600,height=760");
      toast.info("Complete the Xero login in the popup, then click Refresh");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start Xero connection");
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Xero? Invoice/payment sync will stop until reconnected.")) return;
    try {
      await axios.post(`${API}/api/xero/disconnect`, {}, { headers: authHeader() });
      toast.success("Xero disconnected");
      await fetchAll(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disconnect");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const anyValue = Object.values(form).some((v) => v.trim());
    if (!anyValue) {
      toast.error("Enter at least one value to save");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/xero/config`, form, { headers: authHeader() });
      toast.success("Xero settings saved — effective immediately");
      setForm((f) => ({ ...f, client_id: "", client_secret: "" }));
      await fetchAll(false);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5" data-testid="xero-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-brand-navy" />
          <h3 className="font-semibold text-brand-navy">Xero</h3>
        </div>
        <div className="flex items-center gap-2">
          <XeroSetupGuide />
          <Button
            size="sm"
            onClick={() => fetchAll(true)}
            disabled={loading}
            data-testid="xero-test-connection-btn"
            className={status?.connected ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-brand-navy hover:bg-brand-navy-hover text-white"}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            <span className="ml-1.5">Test Connection</span>
          </Button>
        </div>
      </div>

      {/* ── Connection status ── */}
      {status && (
        <div className="flex items-start justify-between gap-3 mb-5 p-3 rounded-md bg-neutral-50 border" data-testid="xero-status-summary">
          <div className="flex items-start gap-3">
            {status.connected ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
            )}
            <div className="text-sm space-y-0.5">
              <p className="font-medium">
                {status.connected ? `Connected to ${status.tenant_name}` : "Not connected"}
              </p>
              {status.connected && (
                <p className="text-muted-foreground text-xs" data-testid="xero-status-hint">
                  Click <strong>Test Connection</strong> to force a live refresh with the current Client ID / Secret — the only real check.
                </p>
              )}
              {status.detail && (
                <p className="text-red-600 text-xs" data-testid="xero-status-detail">{status.detail}</p>
              )}
              {status.connected && status.connected_at && (
                <p className="text-muted-foreground text-xs">Connected {status.connected_at.slice(0, 10)}</p>
              )}
              {config && (
                <p className="text-muted-foreground font-mono text-xs">
                  Client ID: {config.client_id_masked || "not set"} · Secret: {config.client_secret_masked || "not set"}
                </p>
              )}
            </div>
          </div>
          {status.connected ? (
            <Button variant="outline" size="sm" onClick={handleDisconnect} data-testid="xero-disconnect-btn">
              <Unlink className="h-4 w-4 mr-1.5" />Disconnect
            </Button>
          ) : (
            <Button size="sm" onClick={handleConnect} data-testid="xero-connect-btn">
              <Link2 className="h-4 w-4 mr-1.5" />Connect to Xero
            </Button>
          )}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        {/* ── API credentials ── */}
        <div className="text-sm text-muted-foreground space-y-1">
          <p className="font-medium text-foreground">How to get your Xero credentials:</p>
          <ol className="list-decimal list-inside space-y-0.5">
            <li>Log in to the Xero Developer portal (developer.xero.com) with your Xero account</li>
            <li>Go to <span className="font-mono">My Apps → New app</span>, choose <strong>Web app</strong></li>
            <li>Set the <strong>OAuth 2.0 redirect URI</strong> to the exact URI shown below</li>
            <li>Copy the <strong>Client ID</strong>, then click <span className="font-mono">Generate a secret</span> and copy the <strong>Client Secret</strong></li>
          </ol>
          <p>Blank fields keep their current value.</p>
        </div>
        {config?.redirect_uri && (
          <p className="text-xs text-muted-foreground">
            OAuth Redirect URI (register this in your Xero app): <span className="font-mono">{config.redirect_uri}</span>
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="xero-cid">Client ID</Label>
            <Input
              id="xero-cid"
              data-testid="xero-client-id-input"
              autoComplete="off"
              placeholder="Xero app Client ID"
              value={form.client_id}
              onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="xero-csecret">Client Secret</Label>
            <Input
              id="xero-csecret"
              data-testid="xero-client-secret-input"
              type="password"
              autoComplete="off"
              placeholder="Xero app Client Secret"
              value={form.client_secret}
              onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))}
            />
          </div>
        </div>

        {/* ── Account codes ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="xero-sales">Sales Account Code</Label>
            <Input
              id="xero-sales"
              data-testid="xero-sales-account-input"
              placeholder="200"
              value={form.sales_account_code}
              onChange={(e) => setForm((f) => ({ ...f, sales_account_code: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground mt-1">Revenue account for invoice line items</p>
          </div>
          <div>
            <Label htmlFor="xero-fee">Fees Account Code</Label>
            <Input
              id="xero-fee"
              data-testid="xero-fee-account-input"
              placeholder="508"
              value={form.fee_account_code}
              onChange={(e) => setForm((f) => ({ ...f, fee_account_code: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground mt-1">Expense account for Stripe processing fees (your cost)</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Stripe charges book to the Stripe Clearing account and add a &quot;✓ Paid&quot; line on the
          invoice (failed charges go to History &amp; Notes). Manual payments are NOT pushed to Xero —
          mark them received in Xero first, then record them here.
        </p>
        <Button type="submit" disabled={saving} data-testid="xero-save-config-btn">
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          Save Xero Settings
        </Button>
      </form>
    </Card>
  );
};
